<script setup lang="ts">
import { computed, watch } from "vue";
import {
    usePensionOptionsStore,
    EscalationType,
    PaymentArrears,
    ValueProtectOrGuarantee,
    PaymentFrequency,
} from "@/stores/pensionOptions";
import Question from "./fields/Question.vue";
import Select from "./fields/Select.vue";
import RadioSet from "./fields/RadioSet.vue";
import Radio from "./fields/Radio.vue";
import Input from "./fields/Input.vue";
import { tooltips } from "@/tooltips/pensionOptionsTooltips";
import { pensionOptionsValidator } from "@/utils/validators";
import { isDecimal } from "@/utils";
import { useDependantAboutYouStore } from "@/stores/dependantAboutYou";
import { useDependantDailyActivitiesStore } from "@/stores/dependantDailyActivities";
import PensionEligibilityQuestions from "./PensionEligibilityQuestions.vue";
import TaxFreeExceedOptions from "./TaxFreeExceedOptions.vue";
import Modal from "./Modal.vue";

const store = usePensionOptionsStore();
const dependantAboutYouStore = useDependantAboutYouStore();
const dependantDailyActivitiesStore = useDependantDailyActivitiesStore();

const removeDependantFromStore = () => {
    store.spouseDependantPercent = "";
    dependantAboutYouStore.$reset();
    dependantDailyActivitiesStore.$reset();
};

function GetIncreaseRates(): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];
    let increasingRate = 0.5;
    for (let i = 0; i < 20; i++) {
        selectOptions[i] = { label: `${increasingRate.toFixed(1)}`, value: `${increasingRate.toFixed(1)}` };
        increasingRate = increasingRate + 0.5;
    }
    return selectOptions;
}

function Get30Years(): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];

    let years = "Years";
    for (let i = 0; i < 30; i++) {
        if (i === 0) {
            years = "Year";
        } else {
            years = "Years";
        }

        selectOptions[i] = { label: `${i + 1} ${years}`, value: `${i + 1}` };
    }
    return selectOptions;
}

function GetNumericOptions(maxYear = 100): (string | {value: string | number, label: string})[] {
    const selectOptions: (string | {value: string | number, label: string})[] = [];
    for (let i = 0; i < maxYear; i++) {
        selectOptions[i] = { label: `${i + 1}`, value: `${i + 1}` };
    }
    return selectOptions;
}

const validator = pensionOptionsValidator(store);

watch(() => store.valueProtectOrGuarantee, (newValue) => {
    if (newValue !== ValueProtectOrGuarantee.ValueProtection) {
        store.valueProtectionPercent = "";
    }
});
watch(() => store.spouseDependant, (newValue) => {
    if (newValue !== true) {
        store.valueProtectionBasis = "";
    }
});

const maxTaxFreeCash = computed(() =>
    store.taxFreeMaxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

</script>
<template>
    <div
        v-if="store.pensionType === false || store.fundTransferred
            || store.guaranteeAnnuityRate || store.moreThanTwentyFive"
        class="overlay"
    />
    <PensionEligibilityQuestions />
    <hr>
    <h5 class="blue">
        Information
    </h5>
    <div class="container select-your-options-disclaimer">
        <div class="row">
            <div class="col-md-10">
                <p>To get quotes from our panel of providers we need to know:</p>
                <ul>
                    <li>
                        The value of the pension fund(s) you would like to use to purchase your annuity.
                        If you have more than one pension fund you would like to use now, you should add these together.
                    </li>
                    <li>The amount you would like to use to purchase your annuity should include any tax-free lump sum you intend to take prior to purchasing.</li>
                </ul>
                <br>
                <p>Annuity product options may differ for each provider depending on your personal circumstances.</p>
            </div>
        </div>
    </div>
    <Question
        name="totalFund"
        label="What is the total value of the pension fund(s) being used? (including tax-free lump sum)"
        :tooltip="tooltips.totalFund"
    >
        <Input
            v-model.number="store.totalFund"
            type="currency"
            inputmode="text"
            placeholder="0"
            :validation="validator.totalFund"
            :filter="isDecimal"
        />
    </Question>
    <div
        v-if="store.totalFund"
        class="col-12 input-notification"
    >
        <p>
            Please tell us how much tax-free lump sum you want to take. Based on the total pension fund you have stated above, the most you can take is £{{ maxTaxFreeCash }}. This assumes you have not used up your individual Lump Sum Allowance (iLSA).
            <br><br>
            The iLSA limits the amount of tax-free lump sum you can take from all of your pensions. The maximum amount is £268,275.
            <br><br>
            If you have multiple pensions, or a pension fund exceeding £1,073,100 and have taken a tax-free lump sum you will need to calculate the total iLSA you have remaining.
            <br><br>
            For example:
        </p>
        <ul>
            <li>You have 2 pension funds. One with £700,000 and one with £600,000.</li>
            <li>You have taken 25% of £700,000 which is £175,000.</li>
            <li>You want to take the maximum tax-free lump sum from the £600,000. At 25% this would be £150,000. However, the iLSA is limited to £268,275. So, the available tax-free amount on your second fund is £93,275 (i.e. £268,275 less £175,000).</li>
        </ul>
    </div>
    <Question
        type="group"
        name="taxFreeSumFull"
        label="Do you want to take the full amount of allowed tax-free lump sum?"
        :tooltip="tooltips.taxFreeSumFull"
    >
        <RadioSet>
            <Radio
                v-model="store.taxFreeSumFull"
                title="Yes"
                :value="true"
                :validation="validator.taxFreeSumFull"
            />
            <Radio
                v-model="store.taxFreeSumFull"
                title="No"
                :value="false"
                :validation="validator.taxFreeSumFull"
            />
        </RadioSet>
        <div v-if="store.taxFreeSumFull === false">
            <div class="col-12 input-notification">
                <p>You will be using the total fund minus the tax-free lump sum to purchase your annuity.</p>
            </div>
        </div>
    </Question>
    <Question
        v-cloak
        v-if="store.taxFreeSumFull === false"
        name="taxFreeSumSpecific"
        label="How much tax-free lump sum would you like?"
        :tooltip="tooltips.taxFreeSumSpecific"
    >
        <Input
            v-model.number="store.taxFreeSumSpecific"
            type="currency"
            inputmode="text"
            placeholder="Tax Free Sum"
            :validation="validator.taxFreeSumSpecific"
            :filter="isDecimal"
            :reactive-validation="true"
        />
        <div class="col-md-6 tax-free-cash-max">
            <p>Max: £{{ maxTaxFreeCash }}</p>
        </div>
    </Question>
    <TaxFreeExceedOptions />
    <Question
        type="group"
        name="escalation"
        label="Would you like your annuity income to increase each year?"
        :tooltip="tooltips.escalation"
    >
        <RadioSet>
            <Radio
                v-model="store.escalation"
                title="Yes"
                :value="true"
                :validation="validator.escalation"
            />
            <Radio
                v-model="store.escalation"
                title="No"
                :value="false"
                :validation="validator.escalation"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.escalation"
        name="escalationType"
        label="How would you like your annuity income to increase each year?"
        :tooltip="tooltips.escalationType"
    >
        <Select
            v-model="store.escalationType"
            placeholder=" Please Select"
            input-class="w-auto"
            :options="[
                {label: 'Retail Price Index (RPI)', value: EscalationType.RetailPriceIndex},
                {label: 'Limited Price Indexation (LPI)', value: EscalationType.LimitedPriceIndexation},
                {label: 'Fixed', value: EscalationType.Fixed},
            ]"
            :validation="validator.escalationType"
        />
    </Question>
    <Question
        v-if="store.escalation && store.escalationType === EscalationType.Fixed"
        name="escalationAmount"
        label="How much would you like your annuity income to increase by each year?"
        :tooltip="tooltips.escalationAmount"
    >
        <Select
            v-model="store.escalationAmount"
            placeholder="Please select"
            :options="GetIncreaseRates()"
            input-class="w-auto"
            :percentage="true"
            :validation="validator.escalationAmount"
        />
    </Question>
    <Question
        name="paymentFrequency"
        label="How frequently would you like your annuity income payments to be made?"
        :tooltip="tooltips.paymentFrequency"
    >
        <Select
            v-model="store.paymentFrequency"
            placeholder=" Please Select"
            input-class="w-auto"
            :options="[
                {label: 'Monthly', value: PaymentFrequency.Monthly },
                {label: 'Quarterly', value: PaymentFrequency.Quarterly },
                {label: 'Half yearly', value: PaymentFrequency.HalfYearly },
                {label: 'Yearly', value: PaymentFrequency.Yearly }
            ]"
            :validation="validator.paymentFrequency"
        />
    </Question>
    <Question
        type="group"
        name="paymentArrears"
        label=""
    >
        <RadioSet>
            <Radio
                v-model="store.paymentArrears"
                title="In Advance"
                :value="PaymentArrears.InAdvance"
                :validation="validator.paymentArrears"
            />
            <Radio
                v-model="store.paymentArrears"
                title="In Arrears"
                :value="PaymentArrears.InArrears"
                :validation="validator.paymentArrears"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.paymentArrears === PaymentArrears.InArrears && store.paymentFrequency !== PaymentFrequency.Monthly"
        type="group"
        name="proportional"
        label="Do you need a final payment to be made to your named beneficiary,
            for the period from the last payment you received to the date of death?"
        :tooltip="tooltips.proportional"
    >
        <RadioSet>
            <Radio
                v-model="store.proportional"
                title="Yes"
                :value="true"
                :validation="validator.proportional"
            />
            <Radio
                v-model="store.proportional"
                title="No"
                :value="false"
                :validation="validator.proportional"
            />
        </RadioSet>
    </Question>
    <Question
        type="group"
        name="spouseDependant"
        label="Do you need your income to be paid to your named dependant (surviving spouse, registered civil
        partner or financially dependent partner) after your death?"
        :tooltip="tooltips.spouseDependant"
    >
        <RadioSet>
            <Radio
                v-model="store.spouseDependant"
                title="Yes"
                :value="true"
                :validation="validator.spouseDependant"
            />
            <Radio
                v-model="store.spouseDependant"
                title="No"
                :value="false"
                :validation="validator.spouseDependant"
                @change="removeDependantFromStore"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.spouseDependant"
        name="spouseDependantPercent"
        label="What percentage of your annuity do you need to be paid to your named surviving spouse,
        registered civil partner or financially dependent partner?"
        :tooltip="tooltips.spouseDependantPercent"
    >
        <Select
            v-model="store.spouseDependantPercent"
            placeholder="0"
            :options="GetNumericOptions()"
            :percentage="true"
            input-class="w-auto"
            :validation="validator.spouseDependantPercent"
        />
    </Question>
    <Question
        type="group"
        name="valueProtectOrGuarantee"
        label="Do you need value protection or a guarantee period on your annuity?"
    >
        <RadioSet>
            <Radio
                v-model="store.valueProtectOrGuarantee"
                title="Value Protection"
                radio-type="responsive"
                :value="ValueProtectOrGuarantee.ValueProtection"
                :validation="validator.valueProtectOrGuarantee"
            />
            <Radio
                v-model="store.valueProtectOrGuarantee"
                title="Guarantee Period"
                radio-type="responsive"
                :value="ValueProtectOrGuarantee.GuaranteePeriod"
                :validation="validator.valueProtectOrGuarantee"
            />
            <Radio
                v-model="store.valueProtectOrGuarantee"
                title="No Thanks"
                radio-type="responsive"
                :value="ValueProtectOrGuarantee.None"
                :validation="validator.valueProtectOrGuarantee"
            />
        </RadioSet>
        <template #tooltip>
            <p class="mb-1">
                You can add either value protection or a guarantee period
                (also known as a guaranteed minimum payment period), to your annuity to provide some reassurance that:
            </p>
            <ul class="mb-1">
                <li>Should you die after taking out your annuity your estate may receive a lump sum payment.</li>
            </ul>
            <p class="ps-3 mb-1">
                Or
            </p>
            <ul class="mb-3">
                <li>
                    Your annuity may continue to pay at the full amount for a specified number of years,
                    depending what period you select at outset and when you die.
                </li>
            </ul>
            <p class="mb-5">
                Value protection and guarantee periods are optional death benefits that will reduce your initial annuity income.
            </p>
            <p class="mb-3 font-weight-bold">
                Value protection:
            </p>
            <p class="mb-3">
                Value protection allows you to protect all (or part) of the pension fund used to purchase your annuity.
                You can choose to protect up to 100% of your pension fund. If you die without having received the chosen
                percentage of your pension fund, a lump sum payment (minus total gross payments made and tax)
                will be made to your beneficiaries or estate. <a
                    id="valueProtectionExample"
                    href="#"
                    @click.prevent="store.toggleValueProtectionModal = true;"
                >See an example</a>.
            </p>
            <p class="mb-3">
                Note: Different providers have different approaches to value protection, so you should review their
                Key Features documents before purchase to ensure they meet your needs.
            </p>
            <p class="mb-3 font-weight-bold">
                Guarantee period:
            </p>
            <p class="mb-3">
                A guarantee period allows you to ensure your annuity payments will be made for a chosen length of time.
                If you die within your chosen guarantee period, your annuity will continue to be paid for the remainder
                of that term. <a
                    id="guaranteePeriodExample"
                    href="#"
                    @click.prevent="store.toggleGuaranteePeriodModal = true;"
                >See an example</a>.
            </p>
        </template>
    </Question>
    <Question
        v-if="store.valueProtectOrGuarantee === ValueProtectOrGuarantee.ValueProtection"
        name="valueProtectionPercent"
        label="What percentage of your pension fund would you like to protect?"
        :tooltip="tooltips.valueProtectionPercent"
    >
        <Select
            v-model="store.valueProtectionPercent"
            placeholder="0"
            container-class="col-9 col-md-2 p-0"
            :options="GetNumericOptions()"
            :percentage="true"
            :validation="validator.valueProtectionPercent"
        />
    </Question>
    <Question
        v-if="store.valueProtectOrGuarantee === ValueProtectOrGuarantee.ValueProtection && store.spouseDependant"
        type="group"
        name="valueProtectionBasis"
        label="When do you want the protection to end?"
        :tooltip="tooltips.valueProtectionBasis"
    >
        <RadioSet>
            <Radio
                v-model="store.valueProtectionBasis"
                title="My Death"
                value="my-death"
                :validation="validator.valueProtectionBasis"
            />
            <Radio
                v-model="store.valueProtectionBasis"
                title="Last Event"
                value="last-event"
                :validation="validator.valueProtectionBasis"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.valueProtectOrGuarantee === ValueProtectOrGuarantee.GuaranteePeriod"
        name="guaranteePeriodYears"
        label="How long would you like your guarantee period to last?"
        :tooltip="tooltips.guaranteePeriodYears"
    >
        <Select
            v-model="store.guaranteePeriodYears"
            placeholder="0"
            :options="Get30Years()"
            input-class="w-auto"
            :validation="validator.guaranteePeriodYears"
        />
    </Question>
    <div v-if="store.spouseDependant && store.valueProtectOrGuarantee === ValueProtectOrGuarantee.GuaranteePeriod">
        <hr>
        <Question
            type="group"
            name="overlap"
            label="If you were to die during your chosen guarantee period, do you need your
            dependant's income to start immediately or at the end of the guaranteed period?"
        >
            <template #tooltip>
                <p class="mb-1">
                    Immediately - Your named spouse, registered civil partner or financially dependent partner would be paid both:
                </p>
                <ul class="mb-3">
                    <li>The percentage of your income you have chosen for them to receive.</li>
                    <li>The regular annuity payment you were receiving until the end of the guarantee period.</li>
                </ul>
                <p class="mb-3">
                    Choosing this option will reduce the regular income you receive.
                </p>
                <p class="mb-1">
                    After guaranteed period - Your dependant will be paid:
                </p>
                <ul>
                    <li>Your annuity income until the end of the guarantee period and then revert to the annuity percentage you have chosen.</li>
                </ul>
            </template>
            <RadioSet>
                <Radio
                    v-model="store.overlap"
                    title="Immediately"
                    :value="true"
                    :validation="validator.overlap"
                />
                <Radio
                    v-model="store.overlap"
                    title="After Guaranteed Period"
                    :value="false"
                    :validation="validator.overlap"
                />
            </RadioSet>
        </Question>
    </div>
    <Modal
        id="value-protection-modal"
        :show="store.toggleValueProtectionModal"
        header="Value protection"
        @close="() => store.toggleValueProtectionModal = false"
    >
        <template #body>
            <p class="my-2">
                If the only option selected is value protection, when you die, your estate or beneficiaries will receive a lump sum for the difference between the chosen protected percentage of the pension fund and the total annuity income you received before you died.
            </p>
            <p class="my-2">
                For example, suppose you paid £100,000 for your annuity and selected 50% value protection (£50,000). If the total of the income payments you have received was £40,000 at the time of your passing, your chosen beneficiary or estate will receive £10,000 as a lump sum.
            </p>
            <p class="my-2">
                If your policy has already paid out more than the protected amount, there will be no lump sum paid out after your death.
            </p>
            <p class="my-2">
                <span class="font-weight-bold">If you have a joint life annuity what happens with value protection after your death?</span><br>
                Some providers allow value protection to be payable after your death (instead of after the second annuitant’s death). If selected, then in the above example, if you had chosen to leave a 50% income to your surviving spouse or partner as well as a 50% value protection, and the total income payments received was £40,000 by the time of your passing, your chosen beneficiary or estate would receive a £10,000 lump sum.
            </p>
            <p class="my-2">
                Also, your named spouse (or registered civil partner or financially dependent partner) will then start to receive an annual income based on 50% of your income. So, if for example, you received £1,000 a month as annuity income when you were alive, then your named spouse or partner will receive a monthly income of £500 as well as the estate or beneficiary receiving a £10,000 lump sum.
            </p>
            <p class="my-2">
                <span class="font-weight-bold">If you have a joint life annuity what happens with value protection after you both die?</span><br>
                Some providers allow value protection to be payable after both of you die (instead of after the first annuitant’s death). Should you choose to purchase a joint life annuity with the addition of value protection payable on this basis, the value protection will take account of all payments made to both you and your spouse.
            </p>
            <p class="my-2">
                In the above example, if you paid £100,000 for your annuity and selected to leave 50% of your income to your spouse and 50% value protection (£50,000), and the total of the income payments you have received was £40,000 at the time of your passing, payments of 50% of your income would be made to your spouse.
            </p>
            <p class="my-2">
                If at the time of their passing your spouse had received a further £8,000 of income payments (£48,000 of total income payments received), a lump sum amount of £2,000 would be payable to your estate or specified beneficiary.
            </p>
        </template>
    </Modal>
    <Modal
        id="guarantee-period-modal"
        :show="store.toggleGuaranteePeriodModal"
        header="Guarantee period"
        @close="() => store.toggleGuaranteePeriodModal = false"
    >
        <template #body>
            <p class="my-2">
                For example, if you chose a 10-year guarantee period and died after 5 years, your annuity income will continue to be paid to your named beneficiary or estate for a further 5 years after your death. If you die after the selected 10-year guarantee period, nothing is payable after your death (unless you also selected a joint life annuity, and your partner survives you).
            </p>
        </template>
    </Modal>
</template>

<style lang="scss">
    ul {
        margin-bottom: 1rem;
    }
    .select-your-options-disclaimer {
        background-color: #ffffff;
        padding: 20px !important;
        margin-bottom: 20px;

        ul {
            margin-bottom: 0px;
            margin-left: 20px;
        }
    }
    .input-notification {
        color: #000000;
        background-color: #e2e3e5;
        border-color: #d6d8db;
        opacity: 0.9;
        padding: 10px 45px 10px 10px;
        padding: 0.75rem 1.25rem;
        margin-bottom: 1rem;
        margin-top: 1rem;
        border: 1px solid transparent;
        border-radius: 0.25rem;
    }

    .tax-free-cash-max {
        margin: 12px 0;
    }

    .media-player{
        float: right;
    }

    .overlay {
        background: linear-gradient(to bottom, hsla(189, 30%, 80%, 1) , hsla(0, 0%, 80%, 1));
        display: block;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        opacity: 0.6;
        z-index: 1;
    }

    .overlay-question {
        z-index: 2;
        position: relative;
        padding: 1rem;
        background-color: #ffffff;
        margin-bottom: 1rem;

    }

    .overlay-error {
        z-index: 2;
        position: relative;
        padding: 12px;
    }
</style>
